import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon } from 'lucide-react';
interface Product {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl?: string;
}
interface Purchase {
  id: string;
  name: string;
  city: string;
  product: Product;
}
const BUYER_NAMES = ['Carlos M.', 'María G.', 'José R.', 'Ana L.', 'Miguel S.', 'Laura P.', 'David T.', 'Carmen V.', 'Roberto F.', 'Sofia C.', 'Fernando H.', 'Isabel N.', 'Diego A.', 'Patricia B.', 'Ricardo M.', 'Valentina R.', 'Andrés K.', 'Gabriela D.', 'Luis E.', 'Daniela O.', 'Jorge W.', 'Camila Z.', 'Pablo Q.', 'Natalia X.', 'Alejandro Y.'];
const US_CITIES = ['Miami, FL', 'Los Angeles, CA', 'Houston, TX', 'New York, NY', 'Chicago, IL', 'Phoenix, AZ', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA', 'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Boston, MA', 'El Paso, TX', 'Nashville, TN', 'Detroit, MI', 'Portland, OR', 'Las Vegas, NV', 'Memphis, TN', 'Louisville, KY', 'Baltimore, MD', 'Milwaukee, WI', 'Albuquerque, NM', 'Tucson, AZ', 'Fresno, CA'];
export function RecentPurchaseToast() {
  const [currentPurchase, setCurrentPurchase] = useState<Purchase | null>(null);
  const [recentNames, setRecentNames] = useState<string[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  // Scan page for products on mount
  useEffect(() => {
    const scanProducts = (): Product[] => {
      const foundProducts: Product[] = [];
      // Try to find pricing information from the page
      try {
        // Look for price mentions in the DOM
        const priceElements = document.querySelectorAll('[class*="price"], [class*="Price"]');
        const textContent = document.body.innerText;
        // Check for $27 mention (launch price)
        if (textContent.includes('$27') || textContent.includes('27')) {
          foundProducts.push({
            title: 'AI Web Closer System',
            editionLabel: 'Curso Completo',
            priceNumber: 27,
            priceText: '$27',
            thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
          });
        }
        // Check for $297 mention (regular price)
        if (textContent.includes('$297') || textContent.includes('297')) {
          foundProducts.push({
            title: 'AI Web Closer System',
            editionLabel: 'Precio Regular',
            priceNumber: 297,
            priceText: '$297',
            thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
          });
        }
        // Check for AI software pricing
        if (textContent.includes('$20') || textContent.includes('20/mes')) {
          foundProducts.push({
            title: 'AI Web Closer',
            editionLabel: 'Plan Anual',
            priceNumber: 20,
            priceText: '$20/mes',
            thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
          });
        }
        if (textContent.includes('$40') || textContent.includes('40/mes')) {
          foundProducts.push({
            title: 'AI Web Closer',
            editionLabel: 'Plan Mensual',
            priceNumber: 40,
            priceText: '$40/mes',
            thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
          });
        }
      } catch (error) {
        console.warn('Error scanning products:', error);
      }
      // Fallback products if none found
      if (foundProducts.length === 0) {
        return [{
          title: 'AI Web Closer System',
          editionLabel: 'Curso Completo',
          priceNumber: 27,
          priceText: '$27',
          thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
        }, {
          title: 'AI Web Closer System',
          editionLabel: 'Curso Completo',
          priceNumber: 54,
          priceText: '$54',
          thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
        }, {
          title: 'AI Web Closer System',
          editionLabel: 'Curso Completo',
          priceNumber: 91,
          priceText: '$91',
          thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/7qqU33jZeP3k4ipyGfAWpM/ai_web_closer_bundle_mockup.png'
        }];
      }
      return foundProducts;
    };
    // Delay scan to ensure DOM is ready
    setTimeout(() => {
      const scannedProducts = scanProducts();
      setProducts(scannedProducts);
    }, 1000);
  }, []);
  // Generate random purchase
  const generatePurchase = (): Purchase => {
    // Select name (avoid recent ones)
    let availableNames = BUYER_NAMES.filter(name => !recentNames.includes(name));
    if (availableNames.length === 0) {
      availableNames = BUYER_NAMES;
    }
    const name = availableNames[Math.floor(Math.random() * availableNames.length)];
    // Select random city
    const city = US_CITIES[Math.floor(Math.random() * US_CITIES.length)];
    // Select product with weighted distribution
    let product: Product;
    if (products.length === 0) {
      // Fallback with weighted distribution
      const rand = Math.random();
      if (rand < 0.5) {
        product = {
          title: 'AI Web Closer System',
          editionLabel: 'Curso Completo',
          priceNumber: 27,
          priceText: '$27'
        };
      } else if (rand < 0.8) {
        product = {
          title: 'AI Web Closer System',
          editionLabel: 'Curso Completo',
          priceNumber: 54,
          priceText: '$54'
        };
      } else {
        product = {
          title: 'AI Web Closer System',
          editionLabel: 'Curso Completo',
          priceNumber: 91,
          priceText: '$91'
        };
      }
    } else {
      // Weighted selection: 50% = $27, 30% = $54, 20% = $91
      const rand = Math.random();
      const price27 = products.find(p => p.priceNumber === 27);
      const price54 = products.find(p => p.priceNumber === 54);
      const price91 = products.find(p => p.priceNumber === 91);
      if (rand < 0.5 && price27) {
        product = price27;
      } else if (rand < 0.8 && price54) {
        product = price54;
      } else if (price91) {
        product = price91;
      } else {
        product = products[Math.floor(Math.random() * products.length)];
      }
    }
    return {
      id: Date.now().toString(),
      name,
      city,
      product
    };
  };
  // Show notifications
  useEffect(() => {
    if (products.length === 0) return;
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // First notification appears immediately (500ms for render)
    const firstTimeout = setTimeout(() => {
      const purchase = generatePurchase();
      setCurrentPurchase(purchase);
      setRecentNames(prev => [...prev.slice(-9), purchase.name]);
      // Hide after 4 seconds
      setTimeout(() => {
        setCurrentPurchase(null);
      }, 4000);
    }, 500);
    // Recurring notifications every 8-15 seconds
    const scheduleNext = () => {
      const delay = 8000 + Math.random() * 7000; // 8-15 seconds
      return setTimeout(() => {
        const purchase = generatePurchase();
        setCurrentPurchase(purchase);
        setRecentNames(prev => [...prev.slice(-9), purchase.name]);
        // Hide after 4 seconds
        setTimeout(() => {
          setCurrentPurchase(null);
        }, 4000);
        // Schedule next
        const nextTimeout = scheduleNext();
        return () => clearTimeout(nextTimeout);
      }, delay);
    };
    const recurringTimeout = setTimeout(() => {
      scheduleNext();
    }, 500 + 4000); // After first notification completes
    return () => {
      clearTimeout(firstTimeout);
      clearTimeout(recurringTimeout);
    };
  }, [products]);
  return <AnimatePresence data-id="element-458">
      {currentPurchase && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2 z-[60] pointer-events-none" data-id="element-459">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-3 md:p-4 flex items-center gap-3 md:gap-4 w-full md:min-w-[320px] md:max-w-md" data-id="element-460">
            {/* Product Image */}
            {currentPurchase.product.thumbnailUrl && <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-navy rounded-lg overflow-hidden" data-id="element-461">
                <img src={currentPurchase.product.thumbnailUrl} alt={currentPurchase.product.title} className="w-full h-full object-cover" data-id="element-462" />
              </div>}

            {/* Content */}
            <div className="flex-1 min-w-0" data-id="element-463">
              <div className="flex items-start gap-2 mb-1" data-id="element-464">
                <CheckCircleIcon className="h-4 w-4 md:h-5 md:w-5 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-465" />
                <div className="flex-1 min-w-0" data-id="element-466">
                  <p className="font-body text-xs md:text-sm text-gray-900 font-semibold" data-id="element-467">
                    {currentPurchase.name} en {currentPurchase.city}
                  </p>
                  <p className="font-body text-[11px] md:text-xs text-gray-600 mt-0.5" data-id="element-468">
                    compró {currentPurchase.product.editionLabel}
                  </p>
                  <p className="font-heading text-xs md:text-sm font-bold text-gold mt-1" data-id="element-469">
                    {currentPurchase.product.priceText}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}